import tw, { styled } from 'twin.macro';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';

import { IconButton, Title } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';
import { useIsFreemiumUser } from '@restworld/utils-common';

import { EmployerPageContentWrapper } from '../../components/employer-page-content-wrapper';
import DashboardFeedbackCard from './dashboard-feedback-card';
import DashboardReferralCard from './dashboard-referral-card';
import ActivityList from './activity-list';
import RestaurantOpenJobPositionsSection from '../restaurants/details/sections/restaurant-open-job-positions-section';
import useEmployerRestaurantsQuery from '../restaurants/hooks/use-employer-restaurants';
import useEmployerRestaurantCards from '../restaurants/hooks/use-employer-restaurant-cards';
import useMissedOrganisationApplicationActivitiesQuery, {
  useMissedOrganisationApplicationActivities,
} from './hooks/use-missed-organisation-application-activities-query';
import FreemiumUserUpgradeCard from './freemium-user-upgrade-card';
import ShowcaseNextSteps from '../restaurants/details/sections/restuarant-showcase-section/showcase-next-steps';
import { useMemo } from 'react';

function DashboardPageContent() {
  const { t } = useTranslation('dashboard-page');
  const { startTour } = useIntercom();
  const { user } = useAuth();
  const name = user?.name;
  useMissedOrganisationApplicationActivitiesQuery();
  const isFreemiumUser = useIsFreemiumUser();
  const { restaurants } = useEmployerRestaurantsQuery({ enabled: isFreemiumUser });
  const restaurantCards = useEmployerRestaurantCards({ restaurants });
  const hasMissedActivities = useMissedOrganisationApplicationActivities()?.hits > 0;
  const shouldShowActivityList = !isFreemiumUser || (isFreemiumUser && hasMissedActivities);

  const singleRestaurant = useMemo(() => {
    if (restaurants.length === 1) return restaurants[0].restaurant;
    else return null;
  }, [restaurants]);

  return (
    <PageWrapper>
      <MainContentWrapper>
        <ColOneWrapper>
          <Title fontWeight="semibold" textColor="neutral-700" fontSize="superLarge" twStyle={tw`pb-[48px]`}>
            {t('hey_key')}, {name}! 👋
          </Title>
          {hasMissedActivities && (
            <SubHeadingRowWrapper>
              <Title fontWeight="medium" fontSize="base" textColor="neutral-700">
                {t('heres_what_happened_while_you_were_away')}...
              </Title>
              <IconButton
                ariaLabel={t('view_info')}
                Icon={InformationCircleIcon}
                onClick={() => startTour(597872)}
                tw="hidden lg:block" // Intercom product tours don't work from mobile
              />
            </SubHeadingRowWrapper>
          )}
          {shouldShowActivityList && (
            <ColOneContentWrapper tw="mb-16" id="homepage-activity-feed-container">
              <ActivityList />
            </ColOneContentWrapper>
          )}
          {!!restaurantCards.length && (
            <>
              <div>
                <Title textColor="neutral-700" fontWeight="medium" fontSize="base" twStyle={tw`mb-3`}>
                  {t('your_venues')}
                </Title>
                <div tw="flex flex-col gap-4">{isFreemiumUser ? restaurantCards : null}</div>
              </div>
              {!!singleRestaurant && (
                <ShowcaseNextSteps
                  slug={singleRestaurant?.slug}
                  inputRestaurantDetails={singleRestaurant}
                  variant="pyramid"
                  containerTw={tw`!px-0`}
                />
              )}
            </>
          )}
          <RestaurantOpenJobPositionsSection isDashboardView />
        </ColOneWrapper>

        <ColTwoWrapper>
          <ColTwoContentWrapper>
            <CardsWrapper>
              {isFreemiumUser && (
                <CardWrapper>
                  <FreemiumUserUpgradeCard />
                </CardWrapper>
              )}
              <CardWrapper>
                <DashboardReferralCard />
              </CardWrapper>
              <CardWrapper>
                <DashboardFeedbackCard />
              </CardWrapper>
            </CardsWrapper>
          </ColTwoContentWrapper>
        </ColTwoWrapper>
      </MainContentWrapper>
    </PageWrapper>
  );
}

export default DashboardPageContent;

const PageWrapper = styled(EmployerPageContentWrapper)`
  ${tw`xl:(px-10 overflow-visible) lg:(px-20) 2xl:(px-0) bg-neutral-10`};
`;
const MainContentWrapper = styled.div`
  ${tw`w-full flex flex-col gap-16 xl:(flex-row justify-between gap-10) 2xl:(gap-20)`}
`;
const ColOneWrapper = styled.div`
  ${tw`w-full xl:(min-w-[720px] max-w-[750px]) 2xl:(min-w-[880px] )`}
`;
const ColTwoWrapper = styled.div`
  ${tw`w-full flex xl:(justify-end pt-[92px] sticky top-0 h-fit)`}
`;
const SubHeadingRowWrapper = styled.div`
  ${tw`pb-3 flex items-center justify-between`}
`;
const ColOneContentWrapper = styled.div`
  ${tw``}
`;
const ColTwoContentWrapper = styled.div`
  ${tw`w-full xl:(min-w-[20rem] max-w-[20rem] z-10 h-fit)`}
`;
const CardsWrapper = styled.div`
  ${tw`flex flex-col gap-6 md:(flex-row) xl:(flex-col)`}
`;
const CardWrapper = styled.div`
  ${tw`flex-1 flex flex-col`}
`;
